import { useTheme } from "@mui/material";
import { renderToStaticMarkup } from "react-dom/server";
import { AiSearchDocumentInfo } from "../api/types/chatTypes";

const MAX_WIDTH = 200;

// Matches RFC 4122–compliant UUIDs (versions 1–5).
// Enforces the standard 8-4-4-4-12 hex format, including:
// - Version digit [1–5] in the 3rd group
// - Variant digit [8,9,A,B] in the 4th group
// Used to identify and remove UUIDs that are not mapped to known sources.
const UUID_REGEX = /\b[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}\b/g;

function cleanupAfterUuidRemoval(text: string) {
    return (
        text
            // remove extra horizontal whitespace (spaces/tabs) but preserve newlines
            .replace(/[^\S\n\r]{2,}/g, " ")
            // optional: remove empty () or [] that could be left behind
            .replace(/\(\s*\)/g, "")
            .replace(/\[\s*\]/g, "")
            .trim()
    );
}

function willTextOverflow(text: string, maxWidth: number): boolean {
    const measurementSpan = document.createElement("span");
    measurementSpan.style.position = "absolute";
    measurementSpan.style.visibility = "hidden";
    measurementSpan.style.whiteSpace = "nowrap";
    measurementSpan.style.fontSize = "0.75rem";
    measurementSpan.style.fontWeight = "600";
    measurementSpan.style.padding = "0px 8px";
    measurementSpan.style.fontFamily = "Roboto, Arial, sans-serif"; // or match your app

    measurementSpan.textContent = text;

    document.body.appendChild(measurementSpan);
    const width = measurementSpan.offsetWidth;
    document.body.removeChild(measurementSpan);

    return width > maxWidth;
}

export function useAnswerParser() {
    const theme = useTheme();

    function parseAnswerToHtml(answer: string, sources: AiSearchDocumentInfo[]): string {
        let parsedAnswer = answer.trim();

        sources.forEach(source => {
            if (!parsedAnswer.includes(source.id)) return;

            const citationDisplayName = source.citation_display_name;
            const isOverFlowed = willTextOverflow(citationDisplayName, MAX_WIDTH);

            const tag = renderToStaticMarkup(
                <span
                    data-tag={source.citation_display_name}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: "0.75rem",
                        height: "24px",
                        lineHeight: "24px",
                        padding: "0 8px",
                        marginInline: "4px",
                        marginBlock: "0.25rem",
                        borderRadius: "12px",
                        cursor: "pointer",
                        transition: "background-color 0.3s ease",
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.citationBackground,
                        border: `1px solid ${theme.palette.divider}`,
                        maxWidth: `${MAX_WIDTH}px`
                    }}
                    title={isOverFlowed ? citationDisplayName : undefined}
                >
                    <span
                        style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                            minWidth: 0 // important when inside flex
                        }}
                    >
                        {citationDisplayName}
                    </span>
                </span>
            );

            parsedAnswer = parsedAnswer.replaceAll(source.id, tag);
        });

        parsedAnswer = cleanupAfterUuidRemoval(parsedAnswer.replace(UUID_REGEX, ""));

        return parsedAnswer;
    }

    function parseAnswerToText(answer: string, sources: AiSearchDocumentInfo[]): string {
        let parsedAnswer = answer.trim();

        sources.map(source => {
            if (!parsedAnswer.includes(source.id)) return;

            parsedAnswer = parsedAnswer.replaceAll(source.id, `(${source.citation_display_name})`);
        });

        parsedAnswer = cleanupAfterUuidRemoval(parsedAnswer.replace(UUID_REGEX, ""));

        return parsedAnswer;
    }

    // Regex Explanation:
    // This regex finds nested <span> tags and replaces them with just the inner text, wrapped in parentheses.
    // Specifically, it matches the pattern:
    // <span ...> <span ...>inner text</span> </span>
    // and replaces it with:
    // (inner text)
    //
    // Breakdown of the regex:
    // <span[^>]*>         → matches an opening <span> tag with any attributes (non-greedy).
    // \s*                 → matches optional whitespace between the nested tags.
    // <span[^>]*>         → matches the inner <span> tag.
    // ([^<]+)             → captures the actual inner text (anything not a '<' character).
    // <\/span>            → matches the closing tag of the inner span.
    // \s*<\/span>         → matches optional whitespace and the closing tag of the outer span.
    //
    // Example:
    // Input:  <span class="outer"><span class="inner">file.pdf [3]</span></span>
    // Output: (file.pdf [3])
    function cleanHTML(answerHtml: string) {
        return answerHtml.replace(/<span[^>]*>\s*<span[^>]*>([^<]+)<\/span>\s*<\/span>/g, "($1)");
    }

    return { parseAnswerToHtml, parseAnswerToText, cleanHTML };
}
